import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Card, LineChart, Badge } from '@tremor/react';
import { CohortMatrix } from '@/components/CohortMatrix';
import { getRetencaoCohortsMensais, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /retencao/cohorts-mensais) ────────────────────────

interface Ponto {
  periodo: number;
  num: number;
  denom: number;
  pct: number;
}

interface CohortMensal {
  mes: string; // 'YYYY-MM'
  cohort: number;
  voltaram: number;
  serie: Ponto[];
}

interface CohortsData {
  disponivel: boolean;
  erro?: string;
  modo: 'periodico' | 'cumulativo';
  semanas: number;
  segmento: 'todos' | 'pagantes' | 'gratis';
  cohorts: CohortMensal[];
  caveat: string;
}

type Modo = 'periodico' | 'cumulativo';
type Segmento = 'todos' | 'pagantes' | 'gratis';

const MESES_PT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

function mesLabel(mes: string): string {
  const [y, m] = mes.split('-');
  const idx = Number(m) - 1;
  return `${MESES_PT[idx] ?? m}/${y.slice(2)}`;
}

// Rampa frio→quente: safras antigas apagadas, recentes vibrantes — a pergunta
// do gráfico é "as novas estão melhores?", então as novas têm de saltar aos olhos.
const RAMP = [
  'slate', 'gray', 'stone', 'cyan', 'sky', 'blue', 'indigo',
  'violet', 'fuchsia', 'pink', 'rose', 'orange', 'amber', 'emerald',
];

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function Toggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`rounded px-2.5 py-1 text-xs transition ${
            value === o.v
              ? 'bg-blue-600/30 text-blue-200'
              : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function RetencaoCohortsMensaisCard() {
  const [modo, setModo] = useState<Modo>('periodico');
  const [semanas, setSemanas] = useState<'8' | '12' | '26'>('12');
  const [minN, setMinN] = useState<'5' | '1'>('5');
  const [segmento, setSegmento] = useState<Segmento>('todos');

  const { data, isLoading, error } = useQuery<CohortsData>({
    queryKey: ['retencao-cohorts-mensais', modo, semanas, segmento],
    queryFn: async () =>
      (await getRetencaoCohortsMensais({
        modo,
        semanas: Number(semanas),
        segmento,
      })).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar cohorts mensais')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const visiveis = useMemo(
    () => (data?.cohorts ?? []).filter((c) => c.cohort >= Number(minN)),
    [data, minN],
  );

  const { chartData, categories, colors } = useMemo(() => {
    if (visiveis.length === 0)
      return { chartData: [], categories: [], colors: [] };
    const maxP = Math.max(...visiveis.map((c) => c.serie.length - 1));
    const cats = visiveis.map((c) => `${mesLabel(c.mes)} (n=${c.cohort})`);
    // rampa alinhada pela DIREITA: o cohort mais novo sempre pega a cor mais quente
    const cols = visiveis.map(
      (_, i) => RAMP[Math.max(0, RAMP.length - visiveis.length + i)],
    );
    const rows = [];
    for (let n = 0; n <= maxP; n++) {
      const row: Record<string, number | string | null> = { periodo: `sem ${n}` };
      visiveis.forEach((c, i) => {
        const p = c.serie[n];
        row[cats[i]] = p ? +(p.pct * 100).toFixed(1) : null;
      });
      rows.push(row);
    }
    return { chartData: rows, categories: cats, colors: cols };
  }, [visiveis]);

  const matrixCohorts = useMemo(
    () =>
      visiveis.map((c) => ({
        cohort_week: mesLabel(c.mes),
        size: c.cohort,
        retention: c.serie.map((p) => p.pct),
      })),
    [visiveis],
  );

  const ocultos = (data?.cohorts.length ?? 0) - visiveis.length;

  return (
    <section className="space-y-2">
      <Text className="text-xs uppercase tracking-wide text-slate-400">
        Cohorts mensais (safras novas × antigas)
      </Text>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <Title>As safras recentes retêm melhor que as antigas?</Title>
            <Text className="text-xs text-slate-400">
              Cohort = mês do <b>1º download de cálculo</b> da org (t0). Ativo =
              subiu ≥1 caso <b>novo</b> na semana n desde o próprio t0. Eixo Y =
              % do cohort maduro (semana n só conta quem já <b>completou</b> a
              semana n — semana em andamento fica de fora).
            </Text>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Toggle<Modo>
              value={modo}
              onChange={setModo}
              options={[
                { v: 'periodico', label: 'periódico' },
                { v: 'cumulativo', label: 'cumulativo' },
              ]}
            />
            <Toggle<'8' | '12' | '26'>
              value={semanas}
              onChange={setSemanas}
              options={[
                { v: '8', label: '8 sem' },
                { v: '12', label: '12 sem' },
                { v: '26', label: '26 sem' },
              ]}
            />
            <Toggle<'5' | '1'>
              value={minN}
              onChange={setMinN}
              options={[
                { v: '5', label: 'n≥5' },
                { v: '1', label: 'todos' },
              ]}
            />
            <Toggle<Segmento>
              value={segmento}
              onChange={setSegmento}
              options={[
                { v: 'todos', label: 'geral' },
                { v: 'pagantes', label: 'só pagantes' },
                { v: 'gratis', label: 'só grátis' },
              ]}
            />
          </div>
        </div>

        <div className="mb-3 grid gap-1 text-[11px] leading-relaxed text-slate-500 sm:grid-cols-2">
          <div>
            <b>Modo:</b> <i>periódico</i> = % do cohort ativo NA semana n (curva
            de retenção clássica); <i>cumulativo</i> = % que JÁ subiu ≥1 caso
            novo até a semana n.
          </div>
          <div>
            <b>Leitura:</b> cores quentes = safras recentes, frias/apagadas =
            antigas. Linha quente ACIMA da fria na mesma semana = safra nova
            retendo melhor. ⚠️ Tracking de download nasceu 16/06 → sem safras
            antes de jun/26.
          </div>
        </div>

        {segmento !== 'todos' && (
          <div className="mb-3 rounded border border-amber-700/40 bg-amber-900/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200/90">
            ⚠️ <b>Pagante = pagou em ALGUM momento</b> (mesma definição do funil
            e da curva de retorno). Como quase ninguém paga antes do 1º download
            (2/3/1 orgs por safra), a leitura é <b>descritiva, não causal</b>:
            quem volta mais tende a acabar pagando — a curva de pagantes sai
            inflada por construção. Com n pequeno por safra, trate como
            direcional.
          </div>
        )}

        {isLoading && <Text className="mt-3">carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}

        {!isLoading && !errMsg && data?.disponivel && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <Badge color="blue" size="sm">
                {fmtInt(visiveis.length)} cohorts
              </Badge>
              <Badge color="emerald" size="sm">
                {fmtInt(visiveis.reduce((s, c) => s + c.cohort, 0))} orgs
              </Badge>
              {ocultos > 0 && (
                <Badge color="slate" size="sm">
                  {fmtInt(ocultos)} cohorts ocultos (n&lt;5)
                </Badge>
              )}
            </div>

            {chartData.length > 0 ? (
              <LineChart
                className="mt-1 h-80"
                data={chartData}
                index="periodo"
                categories={categories}
                colors={colors}
                valueFormatter={(v) => `${v}%`}
                showAnimation
                connectNulls={false}
                minValue={0}
              />
            ) : (
              <Text className="text-sm text-slate-400">
                nenhum cohort com n≥{minN}
              </Text>
            )}

            <div className="mt-4">
              <CohortMatrix
                cohorts={matrixCohorts}
                title="Matriz (mesmos dados, célula = % ativo na semana)"
                description={data.caveat}
                metric={modo === 'periodico' ? '% ativo' : '% acumulado'}
              />
            </div>
          </>
        )}
      </Card>
    </section>
  );
}
